import React, { memo, useCallback, useState, useMemo } from 'react'
import { Box } from '@material-ui/core'

import { Modal } from 'Components/Modal'
import { ModalBody } from 'Components/Modal/ModalBody'
import { ModalFooter } from 'Components/Modal/ModalFooter'
import { ModalHeader } from 'Components/Modal/ModalHeader'
import { ModalTitle } from 'Components/Modal/ModalTitle'
import Form from 'Components/Form'
import { Input } from 'Components/Form/Input'
import { Textarea } from 'Components/Form/Textarea'
import { SelectDashboard } from 'Components/Form/SelectDashboard'
import SubmitButton from 'Components/Form/SubmitButton'
import Button from 'Components/Form/Button'
import {
  required,
  requiredCommaSeparatedValues,
  number,
} from 'Utils/Validators/formValidation'

import useGraphQLFetch from 'Utils/Hooks/useGraphQLFetch'
import { getCampaigns as getCampaignsGQL, addFlyerCodes } from 'Module/graphQL'
import validateFormAddCode from 'Utils/Validators/addCodeForm'

function AddCodeModal({ showReduxModal, addCodeModalCancel, csvModalShow }) {
  const [pickedCampaign, setPickedCampaign] = useState({})
  const { data = [] } = useGraphQLFetch({
    fetchFn: getCampaignsGQL,
    mapResponse: data =>
      data.campaigns.edges.map(({ node }) => ({
        cityPlans: node.cityPlans.nodes,
        clientId: node.client.id,
        label: node.name,
        value: node.id,
      })),
  })
  const { cities, clientId } = useMemo(() => {
    const campaign =
      data && data.find(campaign => campaign.value === pickedCampaign.value)

    if (campaign) {
      return {
        clientId: campaign.clientId,
        cities: campaign.cityPlans.map(item => ({
          value: item.city.id,
          label: item.city.name,
        })),
      }
    } else {
      return {}
    }
  }, [pickedCampaign.value, data])

  const handleFormUpdate = useCallback(item => {
    if (item.name === 'campaign') {
      setPickedCampaign(item)
    }
  }, [])

  const handleFormSubmit = useCallback(
    form => {
      const { campaign, city, codes, quantity, tag } = form

      const values = {
        clientId,
        campaignId: campaign.value,
        cityId: city.value,
        codes: codes.value,
        quantity: parseInt(quantity.value, 10),
        tag: tag.value,
      }

      try {
        validateFormAddCode(values)
      } catch (e) {
        showReduxModal({
          title: 'Error',
          children: e.message,
          confirmLabel: `Ok`,
          hideCancel: true,
        })
        return
      }

      const flyerCodes = values.codes.split(/[,\s]+/g).filter(code => code)

      const payload = flyerCodes.map(code => ({
        flyerCode: code,
        clientId: values.clientId,
        campaignId: values.campaignId,
        cityId: values.cityId,
        quantity: values.quantity,
        tag: values.tag,
      }))

      showReduxModal({
        title: 'Confirmation needed',
        onConfirm: async () => {
          try {
            await addFlyerCodes(null, payload)
            addCodeModalCancel()
          } catch (err) {
            console.error(err)
          }
        },
        children: confirmationModalBody({
          quantity: values.quantity,
          flyersNumber: payload.length,
        }),
        confirmLabel: `Confirm`,
      })
    },
    [clientId],
  )

  return (
    <Modal show={true}>
      <Form onUpdate={handleFormUpdate} onSubmit={handleFormSubmit}>
        <ModalHeader>
          <ModalTitle>Add flyer codes</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <Box display="flex" flexDirection="column">
            <Box display="flex" mt={3} mb={1.5}>
              <Box width="50%" mr={3}>
                <CampaignSelect compains={data} />
              </Box>
              <Box width="50%" ml={3}>
                <CitySelect cities={cities} />
              </Box>
            </Box>
            <Box display="flex" mt={3} mb={1.5}>
              <Box width="50%" mr={3}>
                <FlyersQuantityInput />
              </Box>
              <Box width="50%" ml={3}>
                <FlyersTagInput />
              </Box>
            </Box>
            <Box width="100%" mt={1.5} mb={1.5}>
              <FlyerCodesTextarea />
            </Box>
          </Box>
        </ModalBody>
        <a
          style={{ marginLeft: 240 }}
          href="javascript:;" // eslint-disable-line
          onClick={csvModalShow}
        >
          or upload as file
        </a>
        <ModalFooter>
          <Button value="cancel" onClick={addCodeModalCancel} />
          <SubmitButton value="confirm" />
        </ModalFooter>
      </Form>
    </Modal>
  )
}
const confirmationModalBody = ({ quantity, flyersNumber }) => (
  <section className="redux-modal">
    <article>
      <p>
        Are you sure? Importing flyer codes each with a quantity of{' '}
        <b style={{ fontSize: 'large' }}>{quantity}</b>. If you get this wrong
        you'll have to adjust{' '}
        <b style={{ fontSize: 'large' }}>
          {Number(flyersNumber).toLocaleString()}
        </b>{' '}
        flyer codes manually.
      </p>
    </article>
  </section>
)

const CampaignSelect = memo(({ compains }) => (
  <SelectDashboard
    label="Campaign"
    name="campaign"
    disabled={!compains}
    validator={[required]}
    options={compains || []}
  />
))

const CitySelect = memo(({ cities }) => (
  <SelectDashboard
    label="City"
    name="city"
    disabled={!cities}
    validator={[required]}
    options={cities || []}
  />
))

const FlyerCodesTextarea = memo(() => (
  <Textarea
    label="Flyer codes"
    name="codes"
    placeholder="code-1,code-2,code-3"
    validator={[requiredCommaSeparatedValues]}
  />
))

const FlyersQuantityInput = memo(() => (
  <Input
    label="Flyer quantity"
    name="quantity"
    placeholder="500"
    validator={[required, number]}
  />
))

const FlyersTagInput = memo(() => (
  <Input label="AB Test" placeholder="Main" name="tag" />
))

export default AddCodeModal



// WEBPACK FOOTER //
// ./src/Containers/Header/Modals/AddCodeModal/index.js